import {
  Box,
  HStack,
  ResponsiveValue,
  Spacer,
  Text,
  VStack,
} from '@chakra-ui/react';
import React, { DragEvent, DragEventHandler, useState } from 'react';
import { PROPERTY_DRAG_DATA_FORMAT } from '../../utils/property-dragging-utils';
import PlaceholderText from '../text/PlaceholderText';
import { useDiffing } from '../project/project-history/hooks/useDiffing';
import { DiffBadge } from '../project/project-history/DiffBadge';
import { EventType } from 'src/gql/graphql';

export type KeyValue = {
  key: string;
  originalKey?: string;
  elementKey?: string;
  value?: React.ReactElement;
  stringValue?: string;
  keyIcon?: React.ReactElement;
  eventType?: EventType;
};

type StyledKeyValuesPropertiesProps = {
  keyValues: KeyValue[];
  getDragData?: (key: string, originalKey?: string) => string;
  mt?: ResponsiveValue<number>;
  mb?: ResponsiveValue<number>;
};

const StyledKeyValuesProperties = ({
  keyValues,
  getDragData,
  mt = 0,
  mb = 2,
}: StyledKeyValuesPropertiesProps): React.ReactElement => {
  const { getBackgroundColor, getTextColor } = useDiffing();

  return (
    <VStack alignItems={'flex-start'} gap={1}>
      {keyValues.length > 0 ? (
        keyValues.map((keyValue) => {
          let dragStartHandler: DragEventHandler | undefined;

          if (getDragData) {
            dragStartHandler = (event: DragEvent<HTMLElement>) => {
              const data = getDragData(keyValue.key, keyValue.originalKey);
              event.dataTransfer.setData(PROPERTY_DRAG_DATA_FORMAT, data);

              // Create a temporary "box" for the drag image
              const dragBox = document.createElement('div');
              dragBox.innerText = keyValue.key;
              dragBox.className = 'sparkel-property-drag-image';

              document.body.appendChild(dragBox);

              event.dataTransfer.effectAllowed = 'copy';
              // Set the temporary box as the drag image
              event.dataTransfer.setDragImage(dragBox, 0, 0);

              // Clean up after the setDragImage call
              setTimeout(function () {
                document.body.removeChild(dragBox);
              }, 0);
            };
          }

          const canDrag = typeof dragStartHandler === 'function';
          return (
            <HStack
              mt={mt}
              key={keyValue.elementKey ?? keyValue.key}
              draggable={canDrag}
              cursor={canDrag ? 'grab' : 'auto'}
              onDragStart={dragStartHandler}
              maxW={'100%'}
              w={'100%'}
              marginLeft={-2}
              paddingLeft={2}
              paddingRight={1}
              paddingY={1}
              borderRadius={4}
              backgroundColor={
                keyValue.eventType
                  ? getBackgroundColor(keyValue.eventType)
                  : undefined
              }
            >
              <VStack alignItems={'flex-start'} gap={0}>
                <HStack
                  color={
                    keyValue.eventType
                      ? getTextColor(keyValue.eventType)
                      : 'gray.500'
                  }
                  fontSize={'xs'}
                >
                  {keyValue.keyIcon && (
                    <Text color={'gray.400'} _dark={{ color: 'gray.500' }}>
                      {keyValue.keyIcon}
                    </Text>
                  )}
                  <Text>{keyValue.key}</Text>
                </HStack>
                {keyValue.stringValue ? (
                  <Box
                    mb={mb}
                    color={'gray.600'}
                    fontSize={'xs'}
                    fontWeight={'bold'}
                    _dark={{
                      color: 'gray.200',
                    }}
                  >
                    {keyValue.stringValue}
                  </Box>
                ) : (
                  <Box
                    color={'gray.600'}
                    _dark={{
                      color: 'gray.200',
                    }}
                  >
                    {keyValue.value}
                  </Box>
                )}
              </VStack>
              {keyValue.eventType && (
                <>
                  <Spacer />
                  <DiffBadge eventType={keyValue.eventType} />
                </>
              )}
            </HStack>
          );
        })
      ) : (
        <PlaceholderText ml={-2} mb={2}>
          Nothing to display
        </PlaceholderText>
      )}
    </VStack>
  );
};

export default StyledKeyValuesProperties;

export const TruncatedValue = ({
  children,
}: {
  children: string;
}): React.ReactElement => {
  const [isTruncated, setIsTruncated] = useState(true);
  return (
    <Text
      mb={2}
      onClick={() => setIsTruncated(!isTruncated)}
      color={'gray.600'}
      fontSize={'xs'}
      fontWeight={'bold'}
      _dark={{
        color: 'gray.200',
      }}
      noOfLines={isTruncated ? 1 : undefined}
    >
      {children}
    </Text>
  );
};
